import { useMemo, useState } from 'react';
import {
    Avatar,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Popover,
    Tooltip,
} from '@material-ui/core';
import { AccountCircle as AccountCircleIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { darken, lighten, alpha } from '@material-ui/core/styles/colorManipulator';
import { t } from '@lingui/macro';

import { useRoleName, useUser, useIsNgpUser } from '../../stores/SessionStore';
import Link from '../links/Link';
import { useNavigate } from '../../utilities/PgmUtilities';

const useStyles = makeStyles((theme) => {
    const isLightTheme = theme.palette.type === 'light';
    const hoverColor = isLightTheme
        ? darken(alpha(theme.palette.background.paper, 1), 0.03)
        : lighten(alpha(theme.palette.background.paper, 1), 0.06);

    return {
        menuButton: {
            padding: 0,
        },
        menu: {
            '& .MuiListItem-root': {
                cursor: 'pointer',

                '&:hover': {
                    backgroundColor: hoverColor,
                },

                '& a:hover': {
                    textDecoration: 'none',
                },
            },

            '& .MuiListItemText-primary': {
                fontSize: 15,
                fontWeight: 400,
                color: theme.palette.text.primary,

                '& a': {
                    display: 'block',
                    fontWeight: 400,
                    color: theme.palette.text.primary,
                },
            },
        },
        profileMenuItem: {
            '& a': {
                ...theme.mixins.flex.displayInlineFlex,
                ...theme.mixins.flex.alignItemsCenter,
            },
        },
    };
});

const anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
};
const transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
};

export default function UserMenu() {
    const classes = useStyles();
    const navigate = useNavigate();
    const [user] = useUser();
    const [roleName] = useRoleName();
    const [isNgpUser] = useIsNgpUser();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const userName = useMemo(() => `${user.firstName} ${user.lastName}`, [user]);
    const [changePasswordUrl, profileUrl] = useMemo(
        () => [`/users/usersPassword/${user.id}`, `/users/usersProfile/${user.id}`],
        [user]
    );

    function handleOpenMenu(event) {
        setMenuAnchor(event.currentTarget);
        event.preventDefault();
    }

    function handleCloseMenu() {
        setMenuAnchor(null);
    }

    const handleProfileClick = () => {
        handleCloseMenu();
        navigate(profileUrl);
    };

    const handleChangePasswordClick = () => {
        handleCloseMenu();
        navigate(changePasswordUrl);
    };

    return (
        <>
            <Tooltip title={t`User Settings`} placement="bottom">
                <IconButton className={classes.menuButton} onClick={handleOpenMenu}>
                    <AccountCircleIcon />
                </IconButton>
            </Tooltip>
            <Popover
                anchorEl={menuAnchor}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                open={menuAnchor !== null}
                onClose={handleCloseMenu}
            >
                <List className={classes.menu}>
                    <ListItem className={classes.profileMenuItem} onClick={handleProfileClick}>
                        <ListItemAvatar>
                            <Avatar>
                                <AccountCircleIcon />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={userName} secondary={roleName} />
                    </ListItem>
                    <Divider />
                    {isNgpUser && (
                        <ListItem onClick={handleChangePasswordClick}>
                            <ListItemText>{t`Change Password`}</ListItemText>
                        </ListItem>
                    )}
                    <ListItem>
                        <ListItemText>
                            <Link to="/logout">{t`Sign Out`}</Link>
                        </ListItemText>
                    </ListItem>
                </List>
            </Popover>
        </>
    );
}
